import { NotificationType } from "@wingspanhq/wingspan-api-sdk";
import { selectorActiveNotifications } from "./selectorActiveNotifications";
import {
  AnyNotificationContext,
  NotificationHandleType,
  WSNotificationResponse
} from "../components/Notifications";

export interface NotificationWithGroup<
  C extends AnyNotificationContext = AnyNotificationContext
> extends WSNotificationResponse<C> {
  group: WSNotificationResponse<C>[];
}

/**
 * Notification handles that must never render as a dashboard alert.
 *
 * - `TaxDocumentsNEC` — hidden by product decision, see PLA-773.
 * - `CollaborationSetupComplete` — retired banner. Its generator was removed
 *   from the notifications service without clearing the rows it had already
 *   created, and every row stores a point-in-time payout destination inside its
 *   message. Undismissed rows therefore keep naming whichever account was the
 *   default when the notification was built, contradicting the live payout
 *   method rendered elsewhere on the same page. See PLA-3452.
 */
const HIDDEN_ALERT_HANDLES: string[] = [
  "TaxDocumentsNEC",
  "CollaborationSetupComplete"
];

const GROUPED_ALERTS = [
  NotificationHandleType.RequirementsAwaitingReviewPayee,
  NotificationHandleType.RequirementsCompletePayee,
  NotificationHandleType.RequirementsRejectedPayee,
  NotificationHandleType.RequirementsReRequestedPayee,
  NotificationHandleType.RequirementsApprovedPayee
];

export const selectorAlerts = (notifications: WSNotificationResponse[]) => {
  const result: NotificationWithGroup[] = [];
  const activeNotifications = selectorActiveNotifications(notifications);
  activeNotifications
    .filter(notification => notification.type === NotificationType.DISMISSABLE)
    .filter(notification => !HIDDEN_ALERT_HANDLES.includes(notification.handle))
    .forEach(notification => {
      if (
        GROUPED_ALERTS.includes(
          notification.handle as NotificationHandleType
        ) &&
        result.some(r => r.handle === notification.handle)
      ) {
        result
          .find(r => r.handle === notification.handle)
          ?.group.push(notification);
      } else {
        result.push({ ...notification, group: [notification] });
      }
    });

  return result;
};
